/** EN catalog — the Care Plan tab (ZOY-667): access states, the week, sessions from Bookings, home tasks. */
export const carePlanEn: Record<string, string> = {
  'rec.tab.care-plan': 'Care Plan',

  // access states
  'cp.never.title': 'No family connected yet',
  'cp.never.body':
    'The care plan is shared through a family connection. Once a family accepts an invitation, this record’s sessions appear here automatically and home tasks can be written for the family.',
  'cp.never.goFamily': 'Open the Family tab',
  'cp.ended.title': 'History — every family connection has ended',
  'cp.ended.body':
    'What was shared while a family was connected stays readable here. Calendar changes are no longer shared, and no new home task can be written until a family connects again.',
  'cp.readOnly.note':
    'You can read this care plan. Writing home tasks needs the plan-author permission.',
  'cp.unavailable.title': 'The plan service can’t be reached right now',
  'cp.unavailable.body':
    'Nothing here is known at the moment — not even whether a family is connected. Try again in a little while.',
  'cp.forbidden.title': 'This tab needs the plan-read permission',
  'cp.forbidden.body': 'Ask the clinic manager to add it to your role.',
  'cp.retry': 'Try again',
  'cp.switchedToHistory':
    'The family connection ended a moment ago — this care plan is now history. Nothing was written.',

  // header
  'cp.title': 'Care Plan',
  'cp.sub':
    'What the connected family sees: the clinic’s sessions, mirrored from the Calendar, and the home tasks the clinic writes for them.',
  'cp.sessionsNote':
    'Sessions come from Bookings. To change one, change the Booking in the Calendar; the plan follows within a few minutes.',
  'cp.eventual': 'A Booking changed a moment ago may not be shown yet.',

  // week strip
  'cp.week.title': 'This week',
  'cp.week.prev': 'Previous week',
  'cp.week.next': 'Next week',
  'cp.week.today': 'This week',
  'cp.week.range': '{from} – {to}',
  'cp.week.empty': 'Nothing scheduled this week.',
  'cp.week.failed': 'The week couldn’t load.',

  // step kinds
  'cp.kind.session': 'Session · from the Calendar',
  'cp.kind.task': 'Home task',
  'cp.session.openBooking': 'Open the Booking',
  'cp.session.practitioner': 'with {name}',
  'cp.session.rescheduled': 'Rescheduled',
  'cp.mode.in_clinic': 'In clinic',
  'cp.mode.off_site': 'Off site',
  'cp.mode.online': 'Online',

  // statuses — Plan's words
  'cp.st.upcoming': 'Upcoming',
  'cp.st.starting_soon': 'Starting soon',
  'cp.st.in_progress': 'In progress',
  'cp.st.awaiting_update': 'Awaiting update',
  'cp.st.completed': 'Completed',
  'cp.st.cancelled': 'Cancelled',
  'cp.st.missed': 'Missed',
  'cp.st.incomplete': 'Incomplete',
  'cp.st.no_longer_updating': 'No longer updating',
  'cp.st.to_do': 'To do',
  'cp.st.due_today': 'Due today',
  'cp.st.overdue': 'Overdue',

  // tasks
  'cp.tasks.title': 'Home tasks',
  'cp.tasks.sub':
    'Written by the clinic for the family to do at home. The family marks them done in their app.',
  'cp.tasks.new': 'New home task',
  'cp.tasks.empty': 'No home tasks yet.',
  'cp.tasks.emptyHint': 'Write the first one — a short, doable thing for this week.',
  'cp.tasks.due': 'Due {when}',
  'cp.tasks.noDue': 'No due date',
  'cp.tasks.by': 'by {name}',
  'cp.tasks.edit': 'Edit',
  'cp.tasks.markDone': 'Mark done',
  'cp.tasks.reopen': 'Reopen',
  'cp.tasks.to.completed': 'Mark done',
  'cp.tasks.to.to_do': 'Reopen',
  'cp.tasks.progress': 'Family progress',
  'cp.tasks.progressNone': 'No progress recorded by the family yet.',
  'cp.tasks.progressFailed': 'Progress couldn’t load.',
  'cp.tasks.evidence': '{n, plural, one {# attachment} other {# attachments}}',
  'cp.tasks.progressBy.family': 'the family',
  'cp.tasks.progressBy.center': 'the clinic',
  'cp.tasks.progressLine': '{who} · {when}',
  'cp.tasks.dueAt': 'Due {when}',
  'cp.tasks.dueDay': 'Due by the end of {when}',
  'cp.evidence.item': 'Attachment {n, number}',
  'cp.evidence.open': 'Open attachment {n, number}',
  'cp.evidence.openTab': 'Open in a new tab {name}',
  'cp.evidence.close': 'Close',
  'cp.evidence.gone': 'This attachment is no longer available.',
  'cp.evidence.unavailable':
    'The evidence store can’t be reached right now. Try again in a moment.',
  'cp.evidence.forbidden': 'You may not open this attachment.',
  'cp.tasks.hide': 'Hide',

  // task dialog
  'cp.form.newTitle': 'New home task',
  'cp.form.editTitle': 'Edit home task',
  'cp.form.title': 'What the family should do',
  'cp.form.titleHelp': 'One concrete thing, in the family’s words.',
  'cp.form.due': 'Due date',
  'cp.form.dueHelp':
    'Optional. Without a time, the task is due by the end of that day, clinic time.',
  'cp.form.time': 'Time (optional)',
  'cp.form.timeHelp': '24-hour, e.g. 18:30.',
  'cp.form.create': 'Add task',
  'cp.form.save': 'Save changes',
  'cp.form.created': 'Home task added',
  'cp.form.saved': 'Home task saved',
  'cp.form.moved': 'Task moved to {status}',

  // errors
  'cp.err.generic': 'The plan service refused this change.',
  'cp.err.sessionNotAuthorable': 'Sessions are changed in the Calendar, not here.',
  'cp.err.connectionEnded': 'The family connection has ended; nothing new can be written.',
  'cp.err.illegal':
    'That move is not allowed from the task’s current state. Allowed now: {allowed}.',
  'cp.err.illegalNone': 'That move is not allowed from the task’s current state.',
  'cp.err.validation': 'The plan service rejected the details. Check the title and date.',
  'cp.err.unavailable': 'The plan service can’t be reached. Nothing was written — try again.',
  'cp.err.contract':
    'The clinic system and the plan service disagree on this change. Nothing was written; the team has been told.',
  'cp.err.forbidden': 'The plan service did not accept this action from you.',
  'cp.err.race': 'Someone else changed this at the same moment. Refresh and try again.',
  'cp.err.retry': 'Try again',
};
